import { computed, effect, EffectRef, inject, Injectable, signal } from '@angular/core';
import { take } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { BsApiService, BsCacheService } from '@brightside-web/desktop/data-access/core-services';
import { ServiceState } from '@brightside-web/desktop/data-access/service-inventory';
import { OnboardingService } from './onboarding.service';
import { SurveyQuestionnaireService } from '@brightside-web/desktop/feature/survey-questionnaire';
import { CreditSetupService } from '@brightside-web/desktop/data-access/credit';
import { DisplayCampaignConfig } from '@brightside-web/shared/display';
import { FirebaseService } from '@brightside-web/desktop/data-access/shared';

interface OnboardingPayload {
  payload: { items: OnboardingItem[] };
}

export interface OnboardingItem {
  key: string;
  order: number;
  route?: string;
  fallback?: { key: string; route: string };
  status?: StepStatus;
}

type StepStatus = 'pending' | 'completed' | 'failed';

@Injectable({
  providedIn: 'root'
})
export class OnboardingStepsService {

  ONBOARDING_COPY: Record<string, DisplayCampaignConfig > = {
    fa_intro: {
      ctaKey: 'BUTTON_CONTINUE',
      image: 'fa_intro_1',
      ctaPath: 'forward',
      key: 'fa_intro',
      titleKey: 'FA_INTRO_TITLE',
      textBodyKey: 'FA_INTRO_DESC',
      textBodyAlign: 'left',
      textBodyNoWrap: true,
      showBackButton: false,
      event_name: 'fa_intro',
      ctaFunction: () => {
        this._currentCopyKey.set('fa_intro_2');
      }
    },
    fa_intro_2: {
      ctaKey: 'I_AM_READY',
      image: 'fa_intro_2',
      event_name: 'fa_intro_2',
      ctaPath: 'complete',
      secondaryCtaPath: 'BACK_WEB',
      key: 'fa_intro_ready',
      titleKey: 'FA_INTRO_READY_TITLE',
      textBodyKey: 'FA_INTRO_READY_DESC',
      showBackButton: true,
      ctaFunction: () => {
        this.advance();
      },
      backFunction: () => {
        this._currentCopyKey.set('fa_intro');
      }
    }
  }


  onboardingCalled = false;
  isLoading = signal(true);


  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _bsApi = inject(BsApiService);
  private _bsCacheService = inject(BsCacheService);
  private _onboardingService = inject(OnboardingService);
  private _surveyQuestionnaireService = inject(SurveyQuestionnaireService);
  private _creditSetupService = inject(CreditSetupService);
  private _amplitude = inject(FirebaseService);


  readonly items = signal<OnboardingItem[] | null>(null);
  private _currentIndex = signal<number>(0);
  readonly currentIndex = this._currentIndex.asReadonly();
  private _currentCopyKey = signal<string | null>(null);

  readonly currentStep = computed(() => {
    const steps = this.items();
    return steps ? steps[this._currentIndex()] ?? null : null;
  });


  readonly currentCopy = computed(() => {
    const step = this.currentStep();
    if (!step) return null;

    // If a sub-step is active, use that instead
    const override = this._currentCopyKey();
    const key = override ?? step.key;

    return this.ONBOARDING_COPY[key] ?? null;
  });
  private inFallback: boolean;
  private isWrappingUp = signal(false);
  private serviceEffect: EffectRef;

  constructor() {
    this._onboardingService.loadInventory();

    this.serviceEffect = effect(
      () => {
        const state = this._onboardingService.state();
        const wrapping = this.isWrappingUp();
        if (!state) return;

        this._bsCacheService.getItem('ONBOARDING_COMPLETE').then((complete) => {
          // Normal onboarding flow
          if (state === ServiceState.INITIAL && !this.onboardingCalled) {
            this._bsCacheService.setItem('ONBOARDING_FLOW_ACTIVE', true);
            this._surveyQuestionnaireService.isOnboarding = true;
            this._amplitude.logEvent('onboarding_started');
            this.loadOnboarding();
          } else if (state === ServiceState.COMPLETE) {
            this._bsCacheService.setItem('ONBOARDING_COMPLETE', true);
            this._router.navigate(['home']);
            this.isWrappingUp.set(false);
          }
        });
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const goHomeCalled = this._surveyQuestionnaireService._goHomeCalled();
      if (goHomeCalled === 'failed') {
        this.failStep();
        this._surveyQuestionnaireService.resetGoHomeCalled();
      } else if (goHomeCalled === 'done') {
        this.completeStep();
        this._surveyQuestionnaireService.resetGoHomeCalled();
      } else if (goHomeCalled) {
        this.back();
        this._surveyQuestionnaireService.resetGoHomeCalled();
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const creditFailed = this._creditSetupService.creditFailed();
      if (creditFailed) {
        const currentStep = this.currentStep();
        if (currentStep?.key === 'credit') {
          this.failStep();
          this._creditSetupService.creditFailed.set(false);
        }
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const creditSucceeded = this._creditSetupService.creditSucceeded();
      if (creditSucceeded) {
        const currentStep = this.currentStep();
        if (currentStep?.key === 'credit') {
          this.completeStep();
        }
      }
    }, { allowSignalWrites: true });
  }

  ngOnDestroy(): void {
    this.serviceEffect.destroy();
  }

  loadOnboarding() {
    if (this.items() !== null) return;

    this._bsApi
      .get<OnboardingPayload>('api-mobile', '/onboarding/v2')
      .pipe(
        take(1)
      )
      .subscribe({
        next: (res) => {
          this.items.set(res.payload.items.map((it) => ({ ...it, status: 'pending' })));
          this._currentIndex.set(0);
          this.onboardingCalled = true;
          this.navigateToCurrent();
        },
        error: (err) => console.error('failed onboarding', err),
      });
  }

  /** Mark current step complete and advance */
  completeStep() {

    const idx = this._currentIndex();
    const steps = this.items();
    if (!steps) return;

    steps[idx] = { ...steps[idx], status: 'completed' };
    this.items.set([...steps]);

    this.advance();
  }

  /** Mark current step failed and handle fallback */
  failStep() {
    const idx = this._currentIndex();
    const steps = this.items();
    if (!steps) return;

    const step = steps[idx];
    steps[idx] = { ...step, status: 'failed' };
    this.items.set([...steps]);
    // Jump to fallback if defined
    if (step.fallback && !this.inFallback) {
      this.navigateToFallback(step.fallback);
    } else {
      this.inFallback = false;
      this.advance();
    }
  }

  /** Move to next step by order */
  advance() {
    this.isLoading.set(true);
    const steps = this.items();
    if (!steps) return;

    const next = this._currentIndex() + 1;
    if (next < steps.length) {
      this._currentCopyKey.set(null); // clear any sub-step override
      this._currentIndex.set(next);
      this.navigateToCurrent();
    } else {
      this.completeOnboarding();
    }
  }


  /** Move backwards */
  back() {
    const prev = this._currentIndex() - 1;
    if (prev >= 0) {
      this._currentCopyKey.set(null);
      this._currentIndex.set(prev);
      this.navigateToCurrent();
    }
  }

  /** Route to whatever step we’re on */
  navigateToCurrent() {
    const step = this.currentStep();
    if (step?.key === 'survey') {
      this.isLoading.set(false);
      this._router.navigate(['onboarding', 'survey', step.route]);
    } else if (step?.key === 'credit') {
      this._router.navigate(['credit-setup', 'creation']);
    } else if (step) {
      this.isLoading.set(false);
      this._router.navigate(['onboarding']);
    }
  }

  private navigateToFallback(fallback: { key: string; route: string }) {
    if (fallback?.key === 'survey') {
      this.isLoading.set(false);
      this.inFallback = true;
      this._router.navigate(['onboarding', 'survey', fallback.route]);
    }
  }

  completeOnboarding() {
    this._router.navigate(['onboarding']);
    this._currentIndex.set(100);

    const steps = this.items();
    if (!steps) return;

    // Collect failed steps into the expected payload shape
    const errors = steps
      .filter(s => s.status === 'failed')
      .map(({ key, route, order }) => ({ key, route, order }));

    const payload = { errors };

    this._bsApi.post('api-mobile', '/onboarding/v2', payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.wrapItUp();
        },
        error: err => {
          this.wrapItUp();
        }
      });
  }

  wrapItUp() {
    this._bsCacheService.setItem('ONBOARDING_COMPLETE', true).then(() => {
      this._amplitude.logEvent('onboarding_completed');
      this._onboardingService.loadInventory();
      this._router.navigate(['home']);
      this.isWrappingUp.set(true);
    });
  }
}
